import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { ClinicalCase } from '@meplis/common';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiSocialV2Service } from '@cnp/services';
import { AppRoutes } from '@cnp/common';

/**
 * Resolves ClinicalCase data before route activation.
 *
 * Angular SSR (RenderMode.Server) waits for this observable to complete
 * before serializing HTML, so "View Page Source" returns fully rendered
 * content including SEO meta tags populated from the resolved entity.
 *
 * Works for:
 *  - Standalone route:  clinical-cases/:id     → reads param 'id'
 *  - Hub-scoped route:  .../clinical-cases/:caseId → reads param 'caseId'
 */
export const clinicalCaseResolver: ResolveFn<ClinicalCase | null> = (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const apiSocialV2 = inject(ApiSocialV2Service);

  // Prefer 'caseId' (hub child routes) over 'id' (standalone route)
  const clinicalCaseId =
    route.paramMap.get('caseId') ?? route.paramMap.get('id');

  if (!clinicalCaseId) {
    router.navigate(['/' + AppRoutes['NOT_FOUND'].fullPath]);
    return EMPTY;
  }

  const clinicalCase$ = apiSocialV2.getClinicalCase(clinicalCaseId).pipe(
    catchError((error) => {
      console.error('Error fetching clinical case in resolver:', error);
      router.navigate(['/' + AppRoutes['NOT_FOUND'].fullPath]);
      return EMPTY;
    })
  );

  return clinicalCase$;
};
